import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Table, Tag, Tooltip, Alert, Spin, Button, Space, Typography, Result, InputNumber } from 'antd';
import { GlobalOutlined, ReloadOutlined, CheckCircleFilled, ExportOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getFuenteOficial,
  type ConsultaFuenteOficialResponse,
  type FarmaciaOficial,
  type ReconciliacionTransmision,
} from '../../api/reconciliacionApi';

const { Text } = Typography;

interface FuenteOficialModalProps {
  visible: boolean;
  onClose: () => void;
  transmision: ReconciliacionTransmision | null;
}

/**
 * GDO-T40 — Consulta bajo demanda de la fuente oficial (p.ej. Sergas) para
 * contrastar los datos de una transmisión. Panel lateral sin máscara y con
 * ancho reajustable (arrastrando el borde izquierdo) para poder ver a la vez
 * la ficha de la transmisión y el listado oficial. Sin persistencia: el
 * veredicto lo pone el usuario.
 */
const FuenteOficialModal: React.FC<FuenteOficialModalProps> = ({ visible, onClose, transmision }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ConsultaFuenteOficialResponse | null>(null);
  const [errorConexion, setErrorConexion] = useState(false);

  // Radio de búsqueda (solo aplica a fuentes por proximidad, p.ej. Farmaguia)
  const RADIO_DEFECTO = 1000;
  const [radio, setRadio] = useState<number>(RADIO_DEFECTO);

  // Ancho reajustable del panel
  const [width, setWidth] = useState(() => Math.min(760, Math.round(window.innerWidth * 0.45)));
  const dragging = useRef(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const nuevo = window.innerWidth - e.clientX;
      setWidth(Math.min(Math.round(window.innerWidth * 0.85), Math.max(420, nuevo)));
      e.preventDefault();
    };
    const onUp = () => { dragging.current = false; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // radioSolicitado explícito: la primera carga va sin radio (usa el defecto del
  // backend); las re-consultas pasan el radio elegido por el usuario. No se
  // incluye `radio` en las deps para no relanzar la consulta al teclear.
  const cargar = useCallback(async (radioSolicitado?: number) => {
    if (!transmision) return;
    setLoading(true);
    setErrorConexion(false);
    setData(null);
    try {
      const resp = await getFuenteOficial(transmision.id, radioSolicitado);
      setData(resp);
      if (resp.radioMetros != null) setRadio(resp.radioMetros);
    } catch {
      setErrorConexion(true);
    } finally {
      setLoading(false);
    }
  }, [transmision]);

  useEffect(() => {
    if (visible) {
      setRadio(RADIO_DEFECTO);
      cargar();
    }
  }, [visible, cargar]);

  const columns: ColumnsType<FarmaciaOficial> = [
    {
      title: 'Siglas',
      dataIndex: 'identificadorOficial',
      width: 90,
      render: (v: string) => v || '—',
    },
    {
      title: 'Titulares',
      dataIndex: 'titulares',
      render: (titulares: FarmaciaOficial['titulares']) => (
        <Space size={[4, 4]} wrap>
          {titulares.map((t, i) => {
            const color = t.coincidencia === 'AT' ? 'green' : t.coincidencia === 'CT' ? 'red' : undefined;
            const tag = (
              <Tag key={i} color={color} icon={t.coincidencia === 'AT' ? <CheckCircleFilled /> : undefined}>
                {t.nombre}{t.coincidencia ? ` (${t.coincidencia})` : ''}
              </Tag>
            );
            return t.coincidencia && t.clienteNombre ? (
              <Tooltip
                key={i}
                title={`Coincide con ${t.coincidencia === 'AT' ? 'titular entrante' : 'titular saliente'}: ${t.clienteNombre}`}
              >
                {tag}
              </Tooltip>
            ) : tag;
          })}
        </Space>
      ),
    },
    {
      title: 'Dirección',
      dataIndex: 'direccion',
      render: (v: string, r: FarmaciaOficial) => (
        <Tooltip title={`Similitud con la dirección de Gescom: ${Math.round(r.scoreDireccion * 100)}%`}>
          <span>{v || '—'}</span>
        </Tooltip>
      ),
    },
    { title: 'CP', dataIndex: 'codigoPostal', width: 70 },
    { title: 'Municipio', dataIndex: 'municipio', width: 130, render: (v: string) => v || '—' },
    { title: 'Teléfono', dataIndex: 'telefono', width: 100, render: (v: string) => v || '—' },
  ];

  const farmacia = transmision?.farmacia;

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      mask={false}
      placement="right"
      width={width}
      zIndex={1001}
      styles={{ body: { paddingTop: 12 } }}
      title={
        <span>
          <GlobalOutlined style={{ marginRight: 8 }} />
          Fuente oficial{data?.fuente ? ` — ${data.fuente}` : ''}
        </span>
      }
    >
      {/* Asa de redimensionado en el borde izquierdo */}
      <div
        onMouseDown={() => { dragging.current = true; document.body.style.userSelect = 'none'; }}
        style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
          cursor: 'ew-resize', zIndex: 10,
        }}
      />

      {/* Datos Gescom de referencia para la comparación lado a lado */}
      {farmacia && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
          <Text strong>{farmacia._Name}</Text>
          <Text type="secondary" style={{ marginLeft: 12 }}>
            {[farmacia.direccion, farmacia.codigoPostal, farmacia.localidad, farmacia.provincia]
              .filter(Boolean).join(' · ') || 'Sin dirección en Gescom'}
          </Text>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin tip="Consultando fuente oficial..." />
        </div>
      )}

      {!loading && errorConexion && (
        <Result
          status="warning"
          title="No se pudo realizar la consulta"
          extra={<Button icon={<ReloadOutlined />} onClick={() => cargar(radio)}>Reintentar</Button>}
        />
      )}

      {!loading && data && (
        <>
          {data.error && (
            <Alert
              type="warning"
              showIcon
              message={data.error}
              action={<Button size="small" icon={<ReloadOutlined />} onClick={() => cargar(radio)}>Reintentar</Button>}
              style={{ marginBottom: 12 }}
            />
          )}

          {!data.tieneCobertura && !data.error && (
            <Alert
              type="info"
              showIcon
              message="Sin cobertura"
              description="No hay fuente oficial disponible para la provincia de esta farmacia."
            />
          )}

          {data.tieneCobertura && !data.error && (
            <>
              {data.soportaRadio && (
                <Space style={{ marginBottom: 12 }} align="center" wrap>
                  <Text type="secondary">Radio de búsqueda:</Text>
                  <InputNumber
                    min={100}
                    max={3000}
                    step={100}
                    value={radio}
                    onChange={(v) => setRadio(v ?? RADIO_DEFECTO)}
                    addonAfter="m"
                    style={{ width: 130 }}
                  />
                  <Button type="primary" icon={<ReloadOutlined />} onClick={() => cargar(radio)}>
                    Buscar
                  </Button>
                </Space>
              )}
              {data.farmacias.length > 0 && !data.farmacias.some(f => f.esMejorMatch) && (
                <Alert
                  type="warning"
                  showIcon
                  message="Ninguna farmacia del listado coincide claramente con la dirección de Gescom"
                  description="Posible código postal incorrecto en Gescom, o calle registrada con otro nombre en la fuente oficial."
                  style={{ marginBottom: 12 }}
                />
              )}
              <Table<FarmaciaOficial>
                dataSource={data.farmacias}
                columns={columns}
                rowKey={(r, i) => r.identificadorOficial || String(i)}
                size="small"
                pagination={false}
                rowClassName={(r) => {
                  // Verde: coincidencia muy alta; naranja: probable pero a revisar
                  if (!r.esMejorMatch) return '';
                  return r.scoreDireccion >= 0.85 ? 'fuente-oficial-match-alto' : 'fuente-oficial-match-medio';
                }}
                locale={{ emptyText: 'La fuente oficial no devuelve farmacias en la zona consultada' }}
              />
              <style>{`
                .fuente-oficial-match-alto td { background-color: #f6ffed !important; }
                .fuente-oficial-match-medio td { background-color: #fff7e6 !important; }
              `}</style>
              {data.urlFuente && (
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <a href={data.urlFuente} target="_blank" rel="noopener noreferrer">
                    Ver en la web oficial <ExportOutlined />
                  </a>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default FuenteOficialModal;
